/**
 * Site model types + small shared helpers. Mirrors the shape the API worker's
 * `GET /sites/:id` returns. Optional fields stay optional.
 */
import { useEffect, useState } from 'hono/jsx';
import { api } from './api';

export interface Subscription { status: 'active' | 'past_due' | string; planName: string; }
export interface Site {
  id: string;
  name: string;
  slug: string;
  email?: string;
  /** Standing per-site build rules, injected into every build's system prompt. */
  prePrompt?: string;
  status: 'draft' | 'building' | 'live' | 'paused' | string;
  buildType?: string | null;
  url: string;
  previewUrl: string;
  /** Custom domain on the site row, or null when only the default subdomain is in use. */
  domain?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  subscription?: Subscription | null;
  canPublish?: boolean;
  isFree?: boolean;
}

/**
 * Card-level summary: a single dot+label that works for paid AND free sites
 * (the old card only showed pills for paid). Returns `null` while the site is
 * mid-build (a separate "Building…" block is shown by the card).
 */
export interface CardStatus { label: string; dot: string; }
export function siteCardStatus(site: Site): CardStatus | null {
  if (site.status === 'building') return null;
  if (site.subscription) {
    const active = site.subscription.status === 'active';
    return {
      label: `${site.subscription.planName} · ${active ? 'Active' : 'Past Due'}`,
      dot: active ? 'bg-emerald-400' : 'bg-amber-400',
    };
  }
  if (site.isFree) {
    if (site.publishedAt) return { label: 'Free · Active', dot: 'bg-emerald-400' };
    if (site.buildType)   return { label: 'Free · Preview', dot: 'bg-cyan-400' };
    return { label: 'Free · Draft', dot: 'bg-slate-400' };
  }
  // Paid plan, no subscription yet — needs payment to be active.
  return { label: 'Payment required', dot: 'bg-amber-400' };
}

export interface StatusBadge { label: string; cls: string; }

/** Derived status badge — `Live` only when actually published. */
export function siteStatusBadge(site: Site): StatusBadge {
  if (site.status === 'building') return { label: 'Building', cls: 'bg-amber-500/20 text-amber-400' };
  if (site.publishedAt) return { label: 'Live', cls: 'bg-emerald-500/20 text-emerald-400' };
  if (site.buildType && site.buildType.length > 0) return { label: 'Preview ready', cls: 'bg-cyan-500/20 text-cyan-400' };
  if (site.status === 'paused') return { label: 'Paused', cls: 'bg-slate-600/30 text-slate-400' };
  return { label: 'Draft', cls: 'bg-slate-600/30 text-slate-300' };
}

/** Fetch a single site by id; returns `null` on 404. */
export function useSite(siteId: string): { site: Site | null; error: string | null; loading: boolean; reload: () => Promise<void> } {
  const [site, setSite] = useState<Site | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api('/sites/' + siteId);
      if (r.status === 404) { setSite(null); setError('not found'); return; }
      if (!r.ok) throw new Error('Failed to load site');
      const data: Site = await r.json();
      setSite(data); setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load site');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [siteId]);

  return { site, error, loading, reload: load };
}
